import PersonData from '@/ts/PersonData';
import AttachmentData from '@/ts/AttachmentData';
import CalendarEventData from '@/ts/CalendarEventData';
import UserResponse from '@/ts/UserResponse';
import { NotificationTypes } from '@/ts/store/Constants';
import moment from 'moment';
import { dateTimeFormat } from './DefaultTableConfigs';
import { CarbonDateTime } from './HococoInterfaces';

export default class FeedItemData {
    public id: number = 0;
    public title: string = '';
    public text: string = '';
    public authorId: number | null;
    public author: any = [];
    public isImportant: boolean | null = null;
    public attachments: AttachmentData[] = [];
    public thumbnailId: number = 0;
    public needsResponse: boolean = false;
    public userResponded: UserResponse | null = null;
    public createdAt: CarbonDateTime | any = null;
    public updatedAt: CarbonDateTime | any = null;
    public typeId: number = null;
    public type: string = '';
    public read: boolean = false;
    public recipients: any[] = [];
    public channel: string = '';

    // Event
    public event: CalendarEventData | null = null;
    public eventId: number | null = null;

    public hasEvent: boolean = false;
    public eventTitle: string = '';
    public eventText: string = '';
    public eventStartTime: string = '';
    public eventEndTime: string = '';

    // Document
    public documentId: number = null;
    public sortingCriterion: number = null;
    public hasMedia: boolean|null = false;
    public likes: number|null = null;
    public comments: number|null = null;
    public likesRecipients: any|null = null;
    public haveLike: boolean|null = null;

    // Event Log
    public eventLog: any = null;
    public publishedAt: CarbonDateTime | any = null;
    public queueIsExecuted: boolean = false;

    public order: any = null;

    constructor(
        id: number,
        title: string = '',
        authorId: number = null,
        author: any = [],
        isImportant: boolean = false,
        text: string = '',
        thumbnailId: number = 0,
        needsResponse: boolean = false,
        userResponded: UserResponse | null = null,
        createdAt: CarbonDateTime | any = null,
        updatedAt: CarbonDateTime | any = null,
        typeId: number = null,
        type: string = null,
        attachments: AttachmentData[] = [],
        read: boolean = false,
        recipients: any[] = [],
        channel: string = '',
        eventLog: any = null,
        publishedAt: string = null,
        order: any = null,
        queueIsExecuted: boolean = false,
    ) {
        this.id = id;
        this.title = title;
        this.authorId = authorId;
        this.author = author;
        this.isImportant = isImportant;
        this.text = text;
        this.thumbnailId = thumbnailId;
        this.needsResponse = needsResponse;
        this.userResponded = userResponded;
        this.createdAt = moment(createdAt).format(dateTimeFormat);
        this.updatedAt = updatedAt;
        this.typeId = typeId;
        this.type = type;
        this.attachments = attachments;
        this.read = read;
        this.recipients = recipients;
        this.channel = channel;
        this.sortingCriterion = Date.parse(this.updatedAt);
        this.eventLog = eventLog;
        this.order = order;
        this.publishedAt = publishedAt;
        this.queueIsExecuted = queueIsExecuted;
    }

    get authorName(): string {
        return this.author ? `${this.author.person.first_name} ${this.author.person.family_name}` : '';
    }

    get isEvent(): boolean {
        return this.type === NotificationTypes.Event;
    }

    get isDocument(): boolean {
        return this.type === NotificationTypes.Document;
    }

    get isInformation(): boolean {
        return this.type === NotificationTypes.Information;
    }

    get isContract(): boolean {
        return this.type === NotificationTypes.Contract;
    }

    get isEventLog(): boolean {
        return this.eventLog !== null;
    }

    get isTicketNotification(): boolean {
        return this.eventLog !== null && typeof JSON.parse(this.eventLog.message).ticket_id !== 'undefined';
    }

    get isOrderNotification(): boolean {
        return this.order !== null;
    }

    get persisted(): boolean {
        return (this.id !== -1);
    }

    get eventTitleText(): string {
        return (this.eventTitle === null) ? 'No' : this.eventTitle;
    }

    get eventStart(): string {
        if (this.eventTitle === null) {
            return '';
        }

        // TODO: Make sure this displays based on proper time zone
        return moment(this.eventStartTime).add(2, 'hours').format('DD-MM-YYYY HH:mm');
    }

    get eventEnd(): string {
        if (this.eventTitle === null) {
            return '';
        }

        // TODO: Make sure this displays based on proper time zone
        return moment(this.eventEndTime).add(2, 'hours').format('DD-MM-YYYY HH:mm');
    }

    get isNotificationOrderOverdue(): boolean {
        return this.isOrderNotification && this.type === 'order_overdue';
    }

    get isNotificationOrderUpcoming(): boolean {
        return this.isOrderNotification && this.type === 'order_upcoming';
    }

    get isNotificationOrderPaid(): boolean {
        return this.isOrderNotification && this.type === 'order_paid';
    }

    get projectNames(): string {
        const recipients: any = this.recipients;
        if (recipients && (!recipients.projects || recipients.projects.length === 0)) {
           return '';
        }
        return recipients.projects.map((project: any) => project.name).join(', ');
    }

    get unitGroupNames(): string {
        const recipients: any = this.recipients;
        if (recipients && (!recipients.buildings || recipients.buildings.length === 0)) {
            return '';
        }
        return recipients.buildings.map((building: any) => building.name).join(', ');
    }

    public setEventData(
        eventTitle: string = '',
        eventText: string = '',
        eventStartTime: string = '',
        eventEndTime: string = '',
    ) {
        this.eventTitle = eventTitle;
        this.eventText = eventText;
        this.eventStartTime = eventStartTime;
        this.eventEndTime = eventEndTime;
        this.hasEvent = true;
    }

    public dataEquals(notification: FeedItemData): boolean {
        // Check if this object's values are equal to the given object's
        if (
            this.id === notification.id &&
            this.title === notification.title &&
            this.authorId === notification.authorId &&
            this.isImportant === notification.isImportant &&
            this.text === notification.text &&
            this.thumbnailId === notification.thumbnailId &&
            this.needsResponse === notification.needsResponse &&
            this.event === notification.event &&
            this.userResponded === notification.userResponded &&
            this.createdAt === notification.createdAt &&
            this.typeId === notification.typeId &&
            this.type === notification.type &&
            this.attachments === notification.attachments &&
            this.read === notification.read &&
            this.eventTitle === notification.eventTitle &&
            this.eventText === notification.eventText &&
            this.eventStartTime === notification.eventStartTime &&
            this.eventEndTime === notification.eventEndTime &&
            this.hasEvent === notification.hasEvent
        ) {
            return false;
        }
    }
}
